// src/pages/DataStructuresPage.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, List, ListItemButton,
  ListItemText, Divider, Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Tooltip, FormControlLabel, Checkbox, FormGroup
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Storage as StorageIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const FIELD_TYPES = [
  'Texto',
  'Texto Largo',
  'Numero Entero',
  'Numero Doble',
  'Fecha',
  'FechaHora'
];

// Sanitiza el nombre del campo: quita tildes, cambia espacios por "_"
// y elimina cualquier caracter que no sea alfanumérico o _.-.
const sanitizeFieldName = (value = '') => (
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_.-]/g, '')
);

export default function DataStructuresPage() {
  const { datastructures, skills } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [items, setItems] = useState([]);            // estructuras
  const [selected, setSelected] = useState(null);    // estructura seleccionada (obj con id, nombre)
  const [fields, setFields] = useState([]);          // campos de la estructura seleccionada
  const [loading, setLoading] = useState(false);
  const [skillList, setSkillList] = useState([]);

  const [openDsDlg, setOpenDsDlg] = useState(false);
  const [editingDs, setEditingDs] = useState(null);

  const [openFieldDlg, setOpenFieldDlg] = useState(false);
  const [editingField, setEditingField] = useState(null);

  // Opciones para mapeo de números del dialer
  const numberOptions = [
    { value: '', label: 'Sin asignar' },
    { value: 'number1', label: 'Number1' },
    { value: 'number2', label: 'Number2' },
    { value: 'number3', label: 'Number3' },
    { value: 'number4', label: 'Number4' }
  ];

  // Función para obtener el mapeo actual de un campo
  const getCurrentMapping = (field) => {
    if (field.is_number1) return 'number1';
    if (field.is_number2) return 'number2';
    if (field.is_number3) return 'number3';
    if (field.is_number4) return 'number4';
    return '';
  };

  // Función para obtener opciones disponibles (excluyendo las ya seleccionadas)
  const getAvailableOptions = (currentFieldId) => {
    const usedMappings = fields
      .filter(f => f.id !== currentFieldId)
      .map(f => getCurrentMapping(f))
      .filter(m => m !== '');
    
    return numberOptions.filter(opt => opt.value === '' || !usedMappings.includes(opt.value));
  };

  // Función para cambiar el mapeo de un campo
  const handleMappingChange = async (fieldId, newMapping) => {
    try {
      if (!ensureUnlocked()) return;
      const fieldData = {
        is_number1: newMapping === 'number1',
        is_number2: newMapping === 'number2',
        is_number3: newMapping === 'number3',
        is_number4: newMapping === 'number4'
      };
      
      await datastructures.updateField(selected.id, fieldId, fieldData);
      
      // Actualizar el estado local
      setFields(prevFields => 
        prevFields.map(f => 
          f.id === fieldId 
            ? { 
                ...f, 
                is_number1: fieldData.is_number1,
                is_number2: fieldData.is_number2,
                is_number3: fieldData.is_number3,
                is_number4: fieldData.is_number4
              }
            : f
        )
      );
      
      showSuccess('Mapeo actualizado correctamente');
    } catch (e) {
      console.error('Error updating mapping:', e);
      showError(`Error actualizando mapeo: ${e.message}`);
    }
  };

  const loadStructures = async (keepSelectedId) => {
    try {
      setLoading(true);
      const [dsRes, skRes] = await Promise.all([
        datastructures.getAll(),
        skills.getAll()
      ]);
      const list = dsRes?.data?.items ?? [];
      setItems(list);
      setSkillList(skRes?.data?.skills ?? []);
      // seleccionar estructura (mantener selección cuando se pueda)
      const idToSelect = keepSelectedId ?? selected?.id ?? list[0]?.id;
      const found = list.find(x => x.id === idToSelect);
      setSelected(found || null);
    } catch (e) {
      console.error(e);
      showError('Error cargando estructuras');
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async (id) => {
    if (!id) { setFields([]); return; }
    try {
      const res = await datastructures.getFields(id);
      setFields(res?.data?.fields ?? []);
    } catch (e) {
      console.error(e);
      showError('Error cargando campos');
    }
  };

  useEffect(() => { loadStructures(); }, []);
  useEffect(() => { if (selected?.id) loadFields(selected.id); }, [selected?.id]);

  const usedStructureIds = useMemo(() => {
    const ids = (skillList || [])
      .map((s) => (s?.iddatastructure ?? s?.dataStructureId ?? s?.datastructure_id ?? null))
      .filter((id) => id !== null && id !== undefined && id !== '');
    return new Set(ids.map((id) => String(id)));
  }, [skillList]);
  const isSelectedLocked = useMemo(
    () => !!(selected?.id && usedStructureIds.has(String(selected.id))),
    [selected, usedStructureIds]
  );
  const lockMessage = 'No se puede modificar: existe al menos un skill usando esta estructura.';

  const ensureUnlocked = () => {
    if (isSelectedLocked) {
      showError(lockMessage);
      return false;
    }
    return true;
  };

  // ====== Dialog Estructura ======
  const {
    register: registerDS, handleSubmit: handleSubmitDS, reset: resetDS, formState: { errors: dsErrors }
  } = useForm({ defaultValues: { nombre: '' } });

  const openCreateDS = () => { setEditingDs(null); resetDS({ nombre: '' }); setOpenDsDlg(true); };
  const openEditDS = () => {
    if (!selected) return;
    if (!ensureUnlocked()) return;
    setEditingDs(selected);
    resetDS({ nombre: selected.nombre });
    setOpenDsDlg(true);
  };
  const onSubmitDS = async (data) => {
    try {
      if (editingDs && !ensureUnlocked()) return;
      if (editingDs) {
        await datastructures.update(editingDs.id, { nombre: data.nombre });
        showSuccess('Estructura actualizada');
      } else {
        await datastructures.create(data.nombre);
        showSuccess('Estructura creada');
      }
      setOpenDsDlg(false);
      await loadStructures(editingDs ? editingDs.id : null);
    } catch (e) {
      console.error(e);
      showError('Error guardando estructura');
    }
  };

  const deleteDS = async () => {
    if (!selected) return;
    if (!ensureUnlocked()) return;
    if (!window.confirm('¿Eliminar la estructura y sus campos?')) return;
    try {
      await datastructures.delete(selected.id);
      showSuccess('Estructura eliminada');
      await loadStructures();
      setFields([]);
    } catch (e) {
      console.error(e);
      showError('Error eliminando estructura');
    }
  };

  // ====== Dialog Campo ======
  const {
    register: registerField,
    handleSubmit: handleSubmitField,
    reset: resetField,
    setValue: setFieldValue,
    watch: watchField,
    formState: { errors: fieldErrors }
  } = useForm({
    defaultValues: { name: '', type: 'Texto', len: 100 }
  });

  const watchedFieldName = watchField('name');

  useEffect(() => {
    const sanitized = sanitizeFieldName(watchedFieldName || '');
    if ((watchedFieldName || '') !== sanitized) {
      setFieldValue('name', sanitized, { shouldValidate: true, shouldDirty: true });
    }
  }, [watchedFieldName, setFieldValue]);

  const openCreateField = () => {
    if (!selected) return;
    if (!ensureUnlocked()) return;
    setEditingField(null);
    resetField({ name: '', type: 'Texto', len: 100 });
    setOpenFieldDlg(true);
  };

  const openEditField = (row) => {
    if (!ensureUnlocked()) return;
    setEditingField(row);
    resetField({ name: row.name, type: row.type, len: row.len });
    setOpenFieldDlg(true);
  };

  const onSubmitField = async (data) => {
    try {
      if (!selected) return;
      if (!ensureUnlocked()) return;
      // Validación suave: si es “Texto Largo” forzamos len=0
      const payload = { ...data, len: data.type === 'Texto Largo' ? 0 : Number(data.len || 0) };

      if (editingField) {
        await datastructures.updateField(selected.id, editingField.id, payload);
        showSuccess('Campo actualizado');
      } else {
        await datastructures.createField(selected.id, payload);
        showSuccess('Campo creado');
      }
      setOpenFieldDlg(false);
      loadFields(selected.id);
    } catch (e) {
      console.error(e);
      showError('Error guardando campo');
    }
  };

  const deleteField = async (row) => {
    if (!selected) return;
    if (!ensureUnlocked()) return;
    if (!window.confirm(`¿Eliminar el campo "${row.name}"?`)) return;
    try {
      await datastructures.deleteField(selected.id, row.id);
      showSuccess('Campo eliminado');
      loadFields(selected.id);
    } catch (e) {
      console.error(e);
      showError('Error eliminando campo');
    }
  };

  const selectedName = useMemo(() => selected?.nombre || '—', [selected]);

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        {/* Panel izquierdo: estructuras */}
        <Grid item xs={12} md={4} lg={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <StorageIcon fontSize="small" />
                  <Typography variant="h6" fontWeight={600}>Estructuras</Typography>
                </Box>
                <Box>
                  <Tooltip title="Refrescar">
                    <IconButton size="small" onClick={() => loadStructures()}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canAction('create_datastructure') && (
                    <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={openCreateDS}>
                      Nueva
                    </Button>
                  )}
                </Box>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <List dense sx={{ maxHeight: 480, overflowY: 'auto' }}>
                {items.map((it) => (
                  <ListItemButton
                    key={it.id}
                    selected={selected?.id === it.id}
                    onClick={() => setSelected(it)}
                  >
                    <ListItemText primary={it.nombre} />
                  </ListItemButton>
                ))}
                {items.length === 0 && <Typography variant="body2" sx={{ p: 1 }}>No hay estructuras.</Typography>}
              </List>

              <Box display="flex" gap={1} mt={1}>
                {canAction('rename_datastructure') && (
                  <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                    <span>
                      <Button onClick={openEditDS} startIcon={<EditIcon />} disabled={!selected || isSelectedLocked}>Renombrar</Button>
                    </span>
                  </Tooltip>
                )}
                {canAction('delete_datastructure') && (
                  <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                    <span>
                      <Button color="secondary" onClick={deleteDS} startIcon={<DeleteIcon />} disabled={!selected || isSelectedLocked}>Eliminar</Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {isSelectedLocked && (
                <Typography variant="caption" color="text.secondary">
                  Estructura en uso por un skill. No se permiten cambios.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel derecho: campos */}
        <Grid item xs={12} md={8} lg={9}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight={600}>Campos de: {selectedName}</Typography>
                {canAction('create_field_datastructure') && (
                  <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                    <span>
                      <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateField} disabled={!selected || isSelectedLocked}>
                        Nuevo Campo
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '30%' }}>Nombre</TableCell>
                      <TableCell style={{ width: '20%' }}>Tipo</TableCell>
                      <TableCell style={{ width: '12%' }}>Tamaño</TableCell>
                      <TableCell style={{ width: '28%' }}>Asignación Dialer</TableCell>
                      <TableCell align="right" style={{ width: '10%' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell>{f.name}</TableCell>
                        <TableCell>{f.type}</TableCell>
                        <TableCell>{f.type === 'Texto Largo' ? '—' : f.len}</TableCell>
                        <TableCell>
                          <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                            <span>
                              <TextField
                                select
                                size="small"
                                value={getCurrentMapping(f)}
                                onChange={(e) => handleMappingChange(f.id, e.target.value)}
                                sx={{ minWidth: 180 }}
                                variant="outlined"
                                disabled={isSelectedLocked}
                              >
                                {getAvailableOptions(f.id).map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {canAction('edit_field_datastructure') && (
                            <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                              <span>
                                <IconButton size="small" onClick={() => openEditField(f)} disabled={isSelectedLocked}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canAction('delete_field_datastructure') && (
                            <Tooltip title={isSelectedLocked ? lockMessage : ''} disableHoverListener={!isSelectedLocked}>
                              <span>
                                <IconButton size="small" color="secondary" onClick={() => deleteField(f)} disabled={isSelectedLocked}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {fields.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">Sin campos</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DLG ESTRUCTURA */}
      <Dialog open={openDsDlg} onClose={() => setOpenDsDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingDs ? 'Renombrar estructura' : 'Nueva estructura'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Nombre de datos"
            autoFocus
            {...registerDS('nombre', { required: 'Requerido' })}
            error={!!dsErrors.nombre}
            helperText={dsErrors.nombre?.message}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDsDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitDS(onSubmitDS)}>
            {editingDs ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DLG CAMPO */}
      <Dialog open={openFieldDlg} onClose={() => setOpenFieldDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingField ? 'Editar campo' : 'Nuevo campo'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                label="Nombre"
                fullWidth
                {...registerField('name', { required: 'Requerido' })}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name?.message}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Tipo"
                fullWidth
                defaultValue="Texto"
                {...registerField('type', { required: 'Requerido' })}
                error={!!fieldErrors.type}
                helperText={fieldErrors.type?.message}
                onChange={(e) => {
                  // Si el usuario elige “Texto Largo”, forzamos len=0
                  const val = e.target.value;
                  if (val === 'Texto Largo') {
                    // set value imperativo (react-hook-form) — usaremos reset en onOpen / onEdit
                  }
                }}
              >
                {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tamaño"
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
                {...registerField('len', {
                  required: 'Requerido',
                  valueAsNumber: true,
                })}
                helperText="Para Texto Largo use 0"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFieldDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitField(onSubmitField)}>
            {editingField ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
